import { Suspense, lazy, useEffect, useState } from 'react'
const HealthView = lazy(() => import('./components/HealthView').then((m) => ({ default: m.HealthView })))
const AccuracyPanel = lazy(() => import('./components/AccuracyPanel').then((m) => ({ default: m.AccuracyPanel })))
const BarometerPanel = lazy(() => import('./components/BarometerPanel').then((m) => ({ default: m.BarometerPanel })))
const InboxView = lazy(() => import('./components/InboxView').then((m) => ({ default: m.InboxView })))
const SkillsView = lazy(() => import('./components/SkillsView').then((m) => ({ default: m.SkillsView })))
const KindsView = lazy(() => import('./components/KindsView').then((m) => ({ default: m.KindsView })))
const JobsView = lazy(() => import('./components/JobsView').then((m) => ({ default: m.JobsView })))
const PlansView = lazy(() => import('./components/PlansView').then((m) => ({ default: m.PlansView })))
const VendorsView = lazy(() => import('./components/VendorsView').then((m) => ({ default: m.VendorsView })))
const OrchestrationView = lazy(() => import('./components/OrchestrationView').then((m) => ({ default: m.OrchestrationView })))
const RunsView = lazy(() => import('./components/RunsView').then((m) => ({ default: m.RunsView })))
const SchedulesView = lazy(() => import('./components/SchedulesView').then((m) => ({ default: m.SchedulesView })))
const RoutingView = lazy(() => import('./components/RoutingView').then((m) => ({ default: m.RoutingView })))
const TracesView = lazy(() => import('./components/TracesView').then((m) => ({ default: m.TracesView })))
const ConnectionsView = lazy(() => import('./components/ConnectionsView').then((m) => ({ default: m.ConnectionsView })))
const SubscribeView = lazy(() => import('./components/SubscribeView').then((m) => ({ default: m.SubscribeView })))
const BudgetsView = lazy(() => import('./components/BudgetsView').then((m) => ({ default: m.BudgetsView })))
const InboxesView = lazy(() => import('./components/InboxesView').then((m) => ({ default: m.InboxesView })))
const QueuesView = lazy(() => import('./components/QueuesView').then((m) => ({ default: m.QueuesView })))
const StaleView = lazy(() => import('./components/StaleView').then((m) => ({ default: m.StaleView })))
const AuditView = lazy(() => import('./components/AuditView').then((m) => ({ default: m.AuditView })))
const AestheticsView = lazy(() => import('./components/AestheticsView').then((m) => ({ default: m.AestheticsView })))
// The story page (#/story) — the scroll-driven product narrative. Deliberately
// NOT in warmRoutes below: it is visited rarely (from the "?" overlay or a
// shared link), and a marketing page must not spend the idle-prefetch budget
// the operator's working routes need.
const StoryView = lazy(() => import('./story/StoryView').then((m) => ({ default: m.StoryView })))
// The token-economics research page (#/research). Public, and out of
// warmRoutes for the same reason StoryView is: a marketing surface must not
// spend the idle-prefetch budget the operator's working routes need.
const ResearchView = lazy(() => import('./research/ResearchView').then((m) => ({ default: m.ResearchView })))
import { ChatConsole } from './components/ChatConsole'
import { PlanProvider } from './plan/PlanProvider'
import { ReactiveBackground } from './components/ReactiveBackground'
import { AssistantPanel } from './components/AssistantPanel'
import { AssistantLauncher } from './components/AssistantLauncher'
import { SignInGate } from './components/SignInGate'
import { UNAUTHENTICATED_EVENT } from './api'
import { ErrorBoundary } from './components/ErrorBoundary'
import { HelpButton } from './components/HelpPanel'
import { CommandPalette, type PaletteResult } from './components/CommandPalette'

type Tab =
  | 'planning'
  | 'accuracy'
  | 'runs'
  | 'queues'
  | 'stale'
  | 'audit'
  | 'health'
  | 'inbox'
  | 'inboxes'
  | 'skills'
  | 'jobs'
  | 'plans'
  | 'orchestration'
  | 'schedules'
  | 'routing'
  | 'traces'
  | 'connections'
  | 'subscribe'
  | 'kinds'
  | 'aesthetics'
  | 'budgets'
  | 'network'
  | 'barometer'
  | 'story'
  | 'research'

// The public pages bring their own grounds — the story has a blueprint grid
// and a dot matrix, the research page has its own figures — and a generic
// bokeh field competes with a 2px drafting line. A named list rather than an
// inline `tab !== 'story'`, because that inline form is how the research page
// would have silently inherited a background nobody chose for it.
const CANVAS_FREE: Tab[] = ['story', 'research']

// The views grouped into a small menu so the operator can tell at a glance which
// surface does what: design the work, run/route it, watch it, or configure it.
// The two surfaces a user lives in — always visible in the top bar.
const PINNED: { id: Tab; label: string }[] = [
  { id: 'planning', label: 'Planning' },
  { id: 'queues', label: 'Inbox' },
]

const GROUPS: { name: string; tabs: { id: Tab; label: string }[] }[] = [
  {
    name: 'Workspace',
    tabs: [
      { id: 'runs', label: 'Runs' },
      { id: 'accuracy', label: 'Accuracy' },
      { id: 'jobs', label: 'Jobs' },
      { id: 'network', label: 'Vendor network' },
      { id: 'orchestration', label: 'Orchestration' },
      { id: 'plans', label: 'Orchestrator plans' },
    ],
  },
  {
    name: 'Operate',
    tabs: [
      { id: 'inbox', label: 'Bus inbox' },
      { id: 'inboxes', label: 'Inboxes' },
      { id: 'routing', label: 'Routing' },
      { id: 'schedules', label: 'Schedules' },
    ],
  },
  {
    name: 'Observe',
    tabs: [
      { id: 'stale', label: 'Stuck work' },
      { id: 'audit', label: 'Audit' },
      { id: 'health', label: 'Health' },
      { id: 'traces', label: 'Traces' },
      { id: 'connections', label: 'Connections' },
      { id: 'budgets', label: 'Budgets' },
      { id: 'barometer', label: 'UX Barometer' },
    ],
  },
  {
    name: 'Configure',
    tabs: [
      { id: 'skills', label: 'Skills' },
      { id: 'aesthetics', label: 'Aesthetics' },
      { id: 'kinds', label: 'Kinds' },
      { id: 'subscribe', label: 'Subscribe' },
    ],
  },
]

// The label the reader saw in the nav, so the sign-in gate names the thing
// they actually clicked rather than an internal tab id.
function labelFor(tab: Tab): string {
  const all = [...PINNED, ...GROUPS.flatMap((g) => g.tabs)]
  return all.find((t) => t.id === tab)?.label ?? 'This view'
}

function viewFor(tab: Tab, deepLink: { tab: Tab; query?: string } | null) {
  const q = deepLink && deepLink.tab === tab ? deepLink.query : undefined
  switch (tab) {
    case 'planning':
      return (
        <ChatConsole />
      )
    case 'accuracy':
      return <AccuracyPanel />
    case 'barometer':
      return <BarometerPanel />
    case 'runs':
      return <RunsView />
    case 'health':
      return <HealthView />
    case 'inbox':
      return <InboxView />
    case 'skills':
      return <SkillsView key={q ?? ''} initialQuery={q} />
    case 'kinds':
      return <KindsView key={q ?? ''} initialQuery={q} />
    case 'aesthetics':
      return <AestheticsView />
    case 'jobs':
      return <JobsView />
    case 'plans':
      return <PlansView />
    case 'network':
      return <VendorsView />
    case 'orchestration':
      return <OrchestrationView key={q ?? ''} initialRunId={q} />
    case 'schedules':
      return <SchedulesView />
    case 'routing':
      return <RoutingView />
    case 'traces':
      return <TracesView />
    case 'connections':
      return <ConnectionsView />
    case 'subscribe':
      return <SubscribeView />
    case 'budgets':
      return <BudgetsView />
    case 'queues':
      return <QueuesView />
    case 'stale':
      return <StaleView />
    case 'audit':
      return <AuditView />
    case 'inboxes':
      return <InboxesView />
    case 'story':
      return <StoryView />
    case 'research':
      return <ResearchView />
  }
}


// Warm the route chunks once the landing screen is interactive.
//
// Splitting cut the cold-load parse by 37%, but it moved the cost rather than
// removing it: the first visit to any other route then waited on a fetch, which
// measured WORSE than the monolith (landing 1500ms → 392ms, every other screen
// → 2524ms). Prefetching during idle keeps both halves of the win — the first
// paint parses only what it needs, and by the time anyone navigates the chunk
// is already in the module cache.
//
// Deliberately after first paint and inside idle time, so it never competes
// with the work the user is actually waiting for.
const warmRoutes = () => {
  const load = () => {
    void import('./components/QueuesView')
    void import('./components/JobsView')
    void import('./components/RunsView')
    void import('./components/AccuracyPanel')
    void import('./components/AestheticsView')
    void import('./components/SkillsView')
    void import('./components/KindsView')
    void import('./components/InboxesView')
    void import('./components/StaleView')
    void import('./components/AuditView')
    void import('./components/HealthView')
    void import('./components/OrchestrationView')
    void import('./components/TracesView')
    void import('./components/SchedulesView')
    void import('./components/RoutingView')
    void import('./components/ConnectionsView')
    void import('./components/SubscribeView')
    void import('./components/BudgetsView')
    void import('./components/VendorsView')
    void import('./components/BarometerPanel')
    void import('./components/InboxView')
    void import('./components/PlansView')
    // The artifact bodies too — a dock click should never wait on a network.
    void import('./components/BidPanel')
    void import('./components/OpenKitchenDiagram')
    void import('./components/TakeoffPanel')
    void import('./components/ModelsPanel')
    void import('./components/VendorNetworkPanel')
    // The document viewer's motion. Deliberately NOT in the initial bundle —
    // nothing on a cold load needs it — but a dock click should never wait on a
    // network either. ScrollTrigger went when the zoom moved to distance-to-
    // centre on the scroll frame; Flip went when animating the expand turned out
    // to fight the zoom for the same element's transform. gsap core is all that
    // is left, and all that is needed.
    void import('gsap')
  }
  if ('requestIdleCallback' in window) {
    ;(window as Window & { requestIdleCallback: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback(load, { timeout: 3000 })
  } else {
    setTimeout(load, 1200)
  }
}

export function App() {
  // The active tab lives in the URL hash (#/queues): browser Back/Forward
  // walk tab history, and any view is deep-linkable.
  const TAB_IDS: Tab[] = ['planning', 'accuracy', 'runs', 'queues', 'stale', 'audit', 'health', 'inbox', 'inboxes', 'skills', 'jobs', 'plans', 'orchestration', 'schedules', 'routing', 'traces', 'connections', 'subscribe', 'kinds', 'aesthetics', 'budgets', 'network', 'barometer', 'story', 'research']
  // A hash-less "/" opens VITE_DEFAULT_TAB when the build set one to a real
  // tab (the public deployment sets "story"), else the operator default.
  const buildDefault = import.meta.env.VITE_DEFAULT_TAB as Tab | undefined
  const defaultTab: Tab = buildDefault && TAB_IDS.includes(buildDefault) ? buildDefault : 'planning'
  const tabFromHash = (): Tab => {
    // The query is stripped BEFORE the membership test. A route may carry
    // in-route state as a query — #/story?v=hustle picks the story's audience
    // — and without this split the whole string "story?v=hustle" misses
    // TAB_IDS and silently falls through to the default tab, so every shared
    // link to a non-default view landed on the planning console.
    const h = window.location.hash.replace(/^#\/?/, '').split('?')[0] as Tab
    return TAB_IDS.includes(h) ? h : defaultTab
  }
  const [tab, rawSetTab] = useState<Tab>(tabFromHash)
  // Which tabs an anonymous visitor may read. Everything else holds real work.
  const PUBLIC_TABS: Tab[] = ['story', 'research']
  // Optimistic: assume a session until the API says otherwise, so an operator
  // never sees a flash of the sign-in gate on a normal load.
  const [authed, setAuthed] = useState(true)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [chromeHidden, setChromeHidden] = useState(false)
  const setTab = (t: Tab) => {
    rawSetTab(t)
    if (window.location.hash !== `#/${t}`) window.location.hash = `#/${t}`
  }
  useEffect(() => {
    const onHash = () => {
      // An IN-PAGE anchor is not a route. `tabFromHash` maps anything it does
      // not recognise to 'planning', so before this check a single
      // `<a href="#trust">` anywhere in the app threw the reader out of the
      // view they were reading and onto the planning console. Route hashes are
      // exactly those beginning `#/`; everything else is the document's own
      // business and the browser has already handled it.
      const h = window.location.hash
      if (h && !h.startsWith('#/')) return
      rawSetTab(tabFromHash())
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // One 401 anywhere means the whole session is anonymous.
  useEffect(() => {
    const onDenied = () => setAuthed(false)
    window.addEventListener(UNAUTHENTICATED_EVENT, onDenied)
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, onDenied)
  }, [])
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [deepLink, setDeepLink] = useState<{ tab: Tab; query?: string } | null>(null)

  useEffect(() => {
    document.body.classList.toggle('chrome-hidden', chromeHidden)
  }, [chromeHidden])

  useEffect(warmRoutes, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key.toLowerCase() === 'f' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        setChromeHidden((c) => !c)
        return
      }
      if (e.key === 'Escape') setChromeHidden(false)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const allViews = GROUPS.flatMap((g) => g.tabs)

  const handleSelect = (r: PaletteResult) => {
    setTab(r.tab as Tab)
    setDeepLink(r.query ? { tab: r.tab as Tab, query: r.query } : null)
    setPaletteOpen(false)
  }

  const openRun = (runId: string) => {
    setTab('orchestration')
    setDeepLink({ tab: 'orchestration', query: runId })
  }

  // A fullscreen artifact (console dock) can't reach this router state directly,
  // so it announces "open this run" and we turn it into the deep-linked switch.
  useEffect(() => {
    const onOpen = (e: Event) => openRun(String((e as CustomEvent).detail))
    window.addEventListener('xnapz:open-run', onOpen)
    return () => window.removeEventListener('xnapz:open-run', onOpen)
  }, [])

  return (
    <PlanProvider>
    {/* Not mounted on the public pages at all (see CANVAS_FREE).
        It used to be dimmed there instead — `opacity: 0.018 !important` — which
        made it invisible and left it running: measured ~10% of a core, forever,
        for a layer nobody could see. Opacity is not an off switch, the same way
        a dimmed layer does not prevent contrast failures (it only hides them
        from axe). Unmounting runs the component's real teardown, so the rAF
        loop stops rather than merely rendering nothing. */}
    {!CANVAS_FREE.includes(tab) && <ReactiveBackground />}
    <div className="app">
      <header className="topbar">
        <h1>xnapz</h1>
        <nav className="nav-groups">
          {/* The two surfaces a user lives in stay pinned; everything else
              lives in compact hover/click menus so the bar never wraps. */}
          {PINNED.map((t) => (
            <button
              key={t.id}
              className={tab === t.id ? 'tab pinned active' : 'tab pinned'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          {GROUPS.map((g) => {
            const groupActive = g.tabs.some((t) => t.id === tab)
            const open = openMenu === g.name
            return (
              <div
                className={`nav-menu${open ? ' open' : ''}${groupActive ? ' has-active' : ''}`}
                key={g.name}
                onMouseEnter={() => setOpenMenu(g.name)}
                onMouseLeave={() => setOpenMenu((m) => (m === g.name ? null : m))}
              >
                <button
                  className={`tab menu-btn${groupActive ? ' active' : ''}`}
                  aria-expanded={open}
                  aria-haspopup="menu"
                  onClick={() => setOpenMenu(open ? null : g.name)}
                >
                  {g.name} <span className="menu-caret">▾</span>
                </button>
                {open ? (
                  <div className="nav-menu-panel" role="menu">
                    {g.tabs.map((t) => (
                      <button
                        key={t.id}
                        role="menuitem"
                        className={tab === t.id ? 'menu-item active' : 'menu-item'}
                        onClick={() => {
                          setTab(t.id)
                          setOpenMenu(null)
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </nav>
        <div className="topbar-actions">
          <AssistantLauncher />
          <button className="search-btn" onClick={() => setPaletteOpen(true)} title="Search (Ctrl/⌘K)">
            Search <kbd>⌘K</kbd>
          </button>
          <button className="search-btn" onClick={() => setChromeHidden(true)} title="Hide all chrome (⇧F toggles, Esc restores)">
            ⛶
          </button>
          <HelpButton />
        </div>
      </header>
      <div className="app-body">
        <main>
          <ErrorBoundary>
            <Suspense fallback={<p className="state">Loading…</p>}>
              {authed || PUBLIC_TABS.includes(tab)
                ? viewFor(tab, deepLink)
                : <SignInGate tabLabel={labelFor(tab)} />}
            </Suspense>
          </ErrorBoundary>
        </main>

      </div>
      {/* ONE assistant, mounted once for the whole app. It used to be three
          different things depending on the route, which is what made the
          product read as having no single voice. */}
      <ErrorBoundary>
        <AssistantPanel />
      </ErrorBoundary>
      {paletteOpen && (
        <CommandPalette views={allViews} onSelect={handleSelect} onClose={() => setPaletteOpen(false)} />
      )}
    </div>
    </PlanProvider>
  )
}
